import React, { useMemo } from 'react';
import {
    FieldOptionDTO,
    UpdateBotToolSettingsRequest
} from "../../../../../../../../api/generated";
import {
    UpdateConditionRequestUI,
    selectConditionComparedValues,
    selectConditionComparedValueAsNumber
} from "../../../toolSettingsSlice";
import { SelectDropdown } from "../../../../../../../elements/select/SelectDropdown";
import { DeleteButton } from "../../../../../../../elements/deleteButton/DeleteButton";
import { ValidationResult } from "../../../../../../../elements/validator/component/ValidationComponent";
import { useConditionState } from "./hooks/useConditionState";
import { useConditionHandlers } from "./hooks/useConditionHandlers";
import { ConditionValueInput } from "./ConditionValueInput";
import styles from "./Condition.module.css";

interface ConditionProps {
    condition: UpdateConditionRequestUI;
    conditionIndex: number;
    groupIndex: number;
    sectionIndex: number;
    fieldOptions: FieldOptionDTO[];
    disabled?: boolean;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

export const Condition: React.FC<ConditionProps> = ({
    condition,
    conditionIndex,
    groupIndex,
    sectionIndex,
    fieldOptions,
    disabled,
    updateValidationStatus,
    getMessageWrapper
}) => {
    const conditionValuesSelector = useMemo(
        () => selectConditionComparedValues(sectionIndex, groupIndex, conditionIndex),
        [sectionIndex, groupIndex, conditionIndex]
    );

    const conditionValueSelector = useMemo(
        () => (state: any) => {
            const values = conditionValuesSelector(state);
            return values && values.length > 0 ? values[0] : undefined;
        },
        [conditionValuesSelector]
    );

    const conditionValueAsNumberSelector = useMemo(
        () => selectConditionComparedValueAsNumber(sectionIndex, groupIndex, conditionIndex),
        [sectionIndex, groupIndex, conditionIndex]
    );

    const fieldName = `modifierRules.ruleSections[${sectionIndex}].conditionGroups[${groupIndex}].conditions[${conditionIndex}].comparedValue` as keyof UpdateBotToolSettingsRequest;

    const state = useConditionState(condition, fieldOptions);
    const handlers = useConditionHandlers(
        condition,
            sectionIndex,
            groupIndex,
            conditionIndex,
        fieldOptions
    );

    return (
        <div className={styles.condition}>
            <div className={styles.field}>
                <SelectDropdown
                    title=""
                    value={condition.conditionField}
                    options={state.fieldOptionsSelect}
                    handleChange={handlers.handleFieldChange}
                    disabled={disabled}
                    selectFirstOptionByDefault={false}
                />
            </div>

            <div className={styles.operator}>
                <SelectDropdown
                    title=""
                    value={condition.operator}
                    options={state.operatorOptions}
                    handleChange={handlers.handleOperatorChange}
                    disabled={disabled || state.operatorOptions.length === 0}
                    selectFirstOptionByDefault={false}
                />
            </div>

            {state.requiresInput && (
                <div className={styles.value}>
                    <ConditionValueInput
                        conditionValues={condition.comparedValues}
                        requiresMultiValue={state.requiresMultiValue}
                        isEnumField={state.isEnumField}
                        isNumericField={state.isNumericField}
                        isRegexOperator={state.isRegexOperator}
                        enumValueOptions={state.enumValueOptions}
                        fieldName={fieldName}
                        conditionValueSelector={conditionValueSelector}
                        conditionValueAsNumberSelector={conditionValueAsNumberSelector}
                        disabled={disabled}
                        updateValidationStatus={updateValidationStatus}
                        getMessageWrapper={getMessageWrapper}
                        onValueChange={handlers.handleValueChange}
                        onMultiValueChange={handlers.handleMultiValueChange}
                    />
                </div>
            )}

            <DeleteButton 
                disabled={disabled}
                handleClick={handlers.handleDelete}
            />
        </div>
    );
};

